import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";

import { PriorityBadge, TicketKey } from "@m2s/design-system";

import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import type { Issue, Project } from "@/lib/types";

// The five lanes, in workflow order. Each renders as its own collapsible
// dropdown section on this page.
const SECTIONS: { key: Issue["status"]; label: string; accent: string }[] = [
  { key: "in_progress", label: "In progress", accent: "#7a3dff" },
  { key: "in_review", label: "In review", accent: "#146ef5" },
  { key: "todo", label: "To do", accent: "var(--mute)" },
  { key: "blocked", label: "Blocked", accent: "#e5484d" },
  { key: "done", label: "Done", accent: "#00d722" },
];

const COLLAPSE_KEY = "m2s.myboard.collapsed";

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

export function MyBoardPage() {
  const me = useAuthStore((s) => s.user);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => readCollapsed());

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed));
    } catch {
      /* noop */
    }
  }, [collapsed]);

  const { data: issues, isLoading } = useQuery({
    queryKey: ["issues", "mine", me?.id],
    queryFn: async () =>
      (await api.get<Issue[]>(`/issues?assignee=${me?.id}&limit=500`)).data,
    enabled: !!me?.id,
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get<Project[]>("/projects")).data,
  });
  const projectById = new Map(projects.map((p) => [p.id, p]));

  const mine = issues ?? [];

  // Bucket my tickets by status.
  const byStatus = useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const i of mine) {
      const arr = map.get(i.status) ?? [];
      arr.push(i);
      map.set(i.status, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
    }
    return map;
  }, [mine]);

  const toggle = (k: string) => setCollapsed((prev) => ({ ...prev, [k]: !prev[k] }));

  const openCount = mine.filter((i) => i.status !== "done").length;

  return (
    <div>
      <header style={{ marginBottom: 8, display: "flex", alignItems: "baseline", gap: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          My board
        </h1>
        <span style={{ fontSize: 13, color: "var(--mute)" }}>
          {mine.length} assigned to you · {openCount} open
        </span>
      </header>
      <p style={{ fontSize: 13, color: "var(--mute)", margin: "0 0 22px" }}>
        Every task assigned to you, grouped by status. Use the arrow on each section to expand or collapse it.
      </p>

      {isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : mine.length === 0 ? (
        <p style={{ color: "var(--mute)" }}>Nothing is assigned to you yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {SECTIONS.map((sec) => {
            const list = byStatus.get(sec.key) ?? [];
            const isOpen = !collapsed[sec.key];
            return (
              <section
                key={sec.key}
                style={{
                  border: "1px solid var(--hairline)",
                  borderRadius: 8,
                  background: "var(--canvas)",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => toggle(sec.key)}
                  aria-expanded={isOpen}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "12px 16px",
                    background: "var(--surface-1)",
                    border: "none",
                    borderBottom: isOpen && list.length ? "1px solid var(--hairline)" : "none",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      color: "var(--mute)",
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      transition: "transform 120ms",
                      transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    }}
                  >
                    ▾
                  </span>
                  <span style={{ width: 8, height: 8, borderRadius: 9999, background: sec.accent }} />
                  <span style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.1px", color: "var(--ink)" }}>
                    {sec.label}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      color: "var(--mute)",
                      background: "var(--surface-2)",
                      padding: "1px 7px",
                      borderRadius: 9999,
                    }}
                  >
                    {list.length}
                  </span>
                </button>

                {isOpen && list.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {list.map((i) => {
                      const proj = projectById.get(i.project_id);
                      return (
                        <Link
                          key={i.id}
                          to={`/issues/${i.key}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 16px",
                            textDecoration: "none",
                            color: "var(--ink)",
                            borderTop: "1px solid var(--hairline)",
                          }}
                        >
                          <TicketKey value={i.key} />
                          <PriorityBadge priority={i.priority} />
                          <span
                            style={{
                              fontSize: 13.5,
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {i.title}
                          </span>
                          {proj && (
                            <span
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: 10.5,
                                color: "var(--mute)",
                                background: "var(--surface-2)",
                                padding: "1px 6px",
                                borderRadius: 3,
                              }}
                            >
                              {proj.key}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}

                {isOpen && list.length === 0 && (
                  <div style={emptyRow}>Nothing here.</div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

const emptyRow: CSSProperties = {
  padding: "12px 16px",
  color: "var(--mute)",
  fontSize: 12.5,
};
